import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Card, CardContent, Table } from '@shared/components';
import { formatVerbatimMoneyCurrency } from '@shared/utils/formatMoneyDisplay';
import type { AbsorbedFeeRowView, AbsorbedFeesView } from '../../types/financials.types';
import { CARD_TABLE_CLASSES } from './cardTableClasses';

interface Props {
    view: AbsorbedFeesView;
    currency: string;
}

const headingId = 'agreement-financials-absorbed-fees';
const COLUMN_WIDTHS = { net: 76, vat: 68, gross: 76 };

export function AbsorbedFeesCard({ view, currency }: Props) {
    const columns = useMemo(() => buildColumns(currency), [currency]);

    return (
        <Card className="h-full">
            <CardContent className="py-5">
                <section aria-labelledby={headingId} className="space-y-3">
                    <h3 id={headingId} className="text-base font-semibold text-mp-foreground">
                        MotorPlan fees
                    </h3>
                    <CardBody view={view} columns={columns} />
                </section>
            </CardContent>
        </Card>
    );
}

function CardBody({ view, columns }: { view: AbsorbedFeesView; columns: ColumnDef<AbsorbedFeeRowView>[] }) {
    if (view.unavailable) {
        return <p className="text-sm text-mp-muted">MotorPlan fees could not be loaded.</p>;
    }
    return (
        <>
            {view.reconstructed && (
                <p className="text-xs text-mp-muted">Fee names are from the current product setup.</p>
            )}
            <Table<AbsorbedFeeRowView>
                data={view.rows}
                columns={columns}
                enableSorting={false}
                getRowId={(entry) => entry.id}
                columnWidths={COLUMN_WIDTHS}
                className={CARD_TABLE_CLASSES}
            />
        </>
    );
}

function buildColumns(currency: string): ColumnDef<AbsorbedFeeRowView>[] {
    const money = (key: 'net' | 'vat' | 'gross', header: string): ColumnDef<AbsorbedFeeRowView> => ({
        id: key,
        header,
        cell: ({ row }) => formatVerbatimMoneyCurrency(row.original[key], currency),
    });
    return [
        {
            id: 'label',
            header: 'Fee',
            cell: ({ row }) => (
                <div>
                    <div>{row.original.label}</div>
                    {row.original.subtitle && (
                        <div className="text-xs text-mp-muted">{row.original.subtitle}</div>
                    )}
                </div>
            ),
        },
        money('net', 'Net'),
        money('vat', 'VAT'),
        money('gross', 'Gross'),
    ];
}
